/**
 * Receipts repository — the ONLY place that touches `receipts` data.
 *
 * ── Migration state ───────────────────────────────────────────────────────────
 *   Step 1 (done) — Consolidated scattered `supabase.from("receipts")` calls
 *                   into this file.
 *   Step 2 (done) — Guarded NestJS endpoints in `apps/api/src/receipts/`
 *                   (`GET /receipts`, `GET /receipts/:id`).
 *   Step 3 (HERE) — `list` + `getById` flipped to NestJS-first with Supabase
 *                   fallback via `reportNestFallback`.
 *
 * ── Flip scope ────────────────────────────────────────────────────────────────
 *   FLIPPED to Nest:     `list`, `getById`
 *   KEPT on Supabase:
 *     `create`           — calls `record_payment` atomic Postgres RPC. Double-entry
 *                          must stay in PG (Fortress rule 2). NEVER re-home this
 *                          casually; the RPC owns the insert + invoice reconciliation
 *                          (including `overpaid` accounting).
 *     `attachDocument`   — tiny post-create update, no Nest endpoint benefit.
 *     `listByInvoice`    — invoice-detail view helper; deferred to the InvoiceDetail slice.
 *     All aggregate/stats/reports methods — cross-domain aggregates that will get
 *       Nest endpoints in the finance-aggregates slice. Deferred backlog:
 *       count, listStats, listSummary, listMonthly, listFinancial, listTotals,
 *       listAmounts, listForOwnerPayouts, listForReports, listRecent, listForCollections.
 *
 * ── Auth split (staff-only flip) ──────────────────────────────────────────────
 *   `list` and `getById` are called only by staff (admin / manager / employee
 *   dashboard). Owners and tenants do NOT call these methods — their access is
 *   RLS-gated via other hooks. The guarded Nest endpoints require `finance.view`;
 *   flipping owner/tenant paths would 403 them, so they stay on Supabase until
 *   dedicated self-service endpoints with appropriate authz are added.
 *
 * ── Employee scope ────────────────────────────────────────────────────────────
 *   The Nest endpoint returns the full company receipt set. Employee property-
 *   assignment scoping (`assignedPropertyIds`) is applied CLIENT-SIDE after
 *   parsing (same pattern as tasks + properties). This avoids a server-side
 *   query parameter that complicates the guard and the Prisma WHERE clause.
 *
 * ── Fallback telemetry ────────────────────────────────────────────────────────
 *   Every fallback is reported via `reportNestFallback` (Sentry) so a silently-
 *   broken Nest endpoint surfaces as an alertable signal instead of invisible
 *   Supabase traffic (the #1699 silent-fallback lesson).
 */
import {
  isDefinitiveNotFound,
  reportNestFallback,
  reportNestFallbackOrRethrowUnknownCommit,
} from "@/lib/nestFallback";
import { z } from "zod";
import { receiptSchema } from "@dallali/shared";
import { supabase } from "@/integrations/supabase/client";
import { serializeError } from "@/lib/sentry";
import type { PaymentChannel, ReceiptWithRelations, TransactionType } from "@/types/database";
import { API_ROUTES } from "@/api/routes";
import { API_URL } from "@/services/api";
import { makeNestRequest } from "@/services/nestRequest";

export interface CreateReceiptData {
  invoice_id?: string;
  contract_id?: string;
  payment_date: string;
  amount: number;
  payment_channel: PaymentChannel;
  transaction_type: TransactionType;
  tenant_id?: string;
  property_id?: string;
  unit_id?: string;
  reference_number?: string;
  notes?: string;
}

interface ReceiptsListParams {
  companyId: string;
  /** Non-null = employee scoped to these property ids; null = no assignment filter. */
  assignedPropertyIds: string[] | null;
}

// Bind the slice's Nest client once — shared by all flipped methods below.
const nestRequest = makeNestRequest("receipts");

// ─── Supabase implementations (kept as fallbacks + for unflipped methods) ────

/**
 * Supabase fallback for `list`. Behaviour-identical to the pre-flip implementation
 * (same select, same filters, same order) — the fallback must be a lossless
 * representation of what the Nest endpoint returns.
 *
 * Employee scope: when `assignedPropertyIds` is a non-empty array, PostgREST
 * applies the `.in("property_id", ids)` filter server-side (same as before).
 */
async function listViaSupabase({
  companyId,
  assignedPropertyIds,
}: ReceiptsListParams): Promise<ReceiptWithRelations[]> {
  let query = supabase
    .from("receipts")
    .select(`
        *,
        tenant:profiles!receipts_tenant_id_fkey(full_name),
        property:properties(name),
        unit:units(unit_number),
        contract:contracts(
          contract_number,
          tenant:tenants(
            first_name,
            last_name,
            first_name_en,
            last_name_en,
            commercial_name,
            commercial_name_en,
            tenant_type
          )
        )
      `)
    .eq("company_id", companyId)
    .is("deleted_at", null);

  if (assignedPropertyIds && assignedPropertyIds.length > 0) {
    query = query.in("property_id", assignedPropertyIds);
  }

  const { data, error } = await query.order("created_at", { ascending: false });

  if (error) {
    throw error;
  }

  return (data ?? []) as unknown as ReceiptWithRelations[];
}

/**
 * Supabase fallback for `getById`. Behaviour-identical to the pre-flip
 * implementation.
 */
async function getByIdViaSupabase(id: string, companyId: string) {
  const { data, error } = await supabase
    .from("receipts")
    .select(`
      *,
      property:properties(name, address),
      unit:units(unit_number),
      contract:contracts(id, contract_number, start_date, tenant_id, owner_id),
      invoice:invoices(invoice_number, due_date, total_amount, description, recipient_role)
    `)
    .eq("id", id)
    .eq("company_id", companyId)
    .is("deleted_at", null)
    .maybeSingle();

  if (error) {
    throw error;
  }

  return data;
}

// ─── The repository ───────────────────────────────────────────────────────────

export const receiptsRepository = {
  /**
   * Canonical receipts list with cross-schema relations, newest first.
   *
   * FLIPPED: tries the guarded `GET /receipts` NestJS endpoint first when
   * `API_URL` is set. Applies employee property-assignment scope client-side
   * after parsing (same pattern as tasks/properties). Falls back to the Supabase
   * path on any Nest error (network / non-2xx / schema parse failure).
   *
   * Always company-scoped + excludes soft-deleted rows. When `assignedPropertyIds`
   * is a non-empty array the result is restricted to those properties.
   */
  async list({ companyId, assignedPropertyIds }: ReceiptsListParams): Promise<ReceiptWithRelations[]> {
    if (API_URL) {
      try {
        const rows = await nestRequest<unknown[]>(API_ROUTES.receipts.list());
        let parsed = z.array(receiptSchema).parse(rows);
        // Apply employee scope client-side — the Nest endpoint returns the full
        // company set; the FE filters by assignedPropertyIds here.
        if (assignedPropertyIds && assignedPropertyIds.length > 0) {
          const allowed = new Set(assignedPropertyIds);
          parsed = parsed.filter((r) => r.property_id != null && allowed.has(r.property_id));
        }
        return parsed as unknown as ReceiptWithRelations[];
      } catch (err) {
        reportNestFallback("receipts.list", err);
      }
    }
    return listViaSupabase({ companyId, assignedPropertyIds });
  },

  /**
   * Fetch a single receipt with full detail relations.
   *
   * FLIPPED: tries `GET /receipts/:id` NestJS endpoint first when `API_URL`
   * is set. Falls back to the Supabase path on any Nest error.
   *
   * Company-scoped. Cross-tenant probe returns null (404 from Nest, not 403).
   */
  async getById(id: string, companyId: string) {
    if (API_URL) {
      try {
        const row = await nestRequest<unknown>(API_ROUTES.receipts.getById(id));
        return row != null
          ? (receiptSchema.parse(row) as unknown as Awaited<ReturnType<typeof getByIdViaSupabase>>)
          : null;
      } catch (err) {
        if (isDefinitiveNotFound(err)) return null; // app-level 404 = the real answer (#3397 class)
        reportNestFallback("receipts.getById", err);
      }
    }
    return getByIdViaSupabase(id, companyId);
  },

  // ─── Money mutations (atomic Postgres RPCs — wrapped by Nest, Supabase fallback) ─

  /**
   * Record a payment via `record_payment` (atomic double-entry in Postgres).
   *
   * FLIPPED: tries the guarded `POST /receipts` NestJS endpoint first when
   * `API_URL` is set (the endpoint wraps `record_payment` via Prisma `$queryRaw`
   * — the RPC still owns the receipt insert + invoice fan-out incl. `overpaid`,
   * so atomicity is preserved; Fortress rule 2 holds). Falls back to the direct
   * Supabase RPC on any Nest error (network / non-2xx / config).
   *
   * `idempotencyKey` (optional): forwarded as the `Idempotency-Key` header so a
   * replayed submit returns the original receipt instead of creating a second one
   * (the Supabase fallback path has no equivalent, so keep the caller's own
   * double-submit guard). company_id is derived server-side by the endpoint; the
   * fallback still passes it explicitly to the RPC.
   *
   * Duplicate-payment guard (#3360): this is a MONEY write, so the fallback only
   * runs when Nest verifiably did not commit (4xx / unconfigured). An ambiguous
   * outcome (network, timeout, 5xx) RETHROWS instead of re-issuing the payment
   * through the RPC — the Idempotency-Key does not span the fallback path, so a
   * fallback after a committed-but-lost Nest write would double-count the payment
   * (the #3316 properties.create failure shape, applied to money).
   */
  async create(
    companyId: string,
    receiptData: CreateReceiptData,
    idempotencyKey?: string,
  ) {
    if (API_URL) {
      try {
        return await nestRequest<unknown>(API_ROUTES.receipts.create(), {
          method: "POST",
          body: JSON.stringify(receiptData),
          ...(idempotencyKey
            ? { headers: { "Idempotency-Key": idempotencyKey } }
            : {}),
        });
      } catch (err) {
        reportNestFallbackOrRethrowUnknownCommit("receipts.create", err);
      }
    }

    const { data, error } = await supabase.rpc("record_payment", {
      p_company_id: companyId,
      p_amount: receiptData.amount,
      p_payment_channel: receiptData.payment_channel,
      p_transaction_type: receiptData.transaction_type,
      p_payment_date: receiptData.payment_date,
      p_invoice_id: receiptData.invoice_id || undefined,
      p_contract_id: receiptData.contract_id || undefined,
      p_tenant_id: receiptData.tenant_id || undefined,
      p_property_id: receiptData.property_id || undefined,
      p_unit_id: receiptData.unit_id || undefined,
      p_reference_number: receiptData.reference_number || undefined,
      p_notes: receiptData.notes || undefined,
    });

    if (error) {
      console.error("Error creating receipt:", serializeError(error));
      throw error;
    }

    return data;
  },

  /**
   * Soft-delete a receipt AND reverse its effect on the parent invoice via
   * `delete_receipt_and_reconcile` (atomic in Postgres: receipt soft-delete +
   * invoice paid_amount/status rewrite + scheduled_payment sync).
   *
   * FLIPPED: tries the guarded `DELETE /receipts/:id` NestJS endpoint first when
   * `API_URL` is set (the endpoint wraps the RPC via Prisma `$queryRaw`). Falls
   * back to the direct Supabase RPC only when Nest verifiably did not commit
   * (4xx / unconfigured); an ambiguous outcome rethrows (#3360 duplicate-write
   * guard) — a replayed reconcile against an already-deleted receipt could
   * double-reverse the invoice. company_id is derived server-side by the
   * endpoint; the fallback passes it explicitly to the RPC.
   */
  async deleteAndReconcile(companyId: string, receiptId: string) {
    if (API_URL) {
      try {
        await nestRequest<unknown>(API_ROUTES.receipts.delete(receiptId), {
          method: "DELETE",
        });
        return;
      } catch (err) {
        reportNestFallbackOrRethrowUnknownCommit("receipts.deleteAndReconcile", err);
      }
    }

    // `as any` cast: generated supabase types lag the RPC until the migration
    // applies in CI/CD (same pattern as update_contract_with_invoices).
    const { error } = await (
      supabase as unknown as {
        rpc: (...args: unknown[]) => Promise<{ error: unknown }>;
      }
    ).rpc("delete_receipt_and_reconcile", {
      p_company_id: companyId,
      p_receipt_id: receiptId,
    });

    if (error) {
      console.error("Error deleting receipt:", serializeError(error));
      throw error;
    }
  },

  /**
   * Attach a document URL (payment proof) to a receipt after creation.
   *
   * KEPT ON SUPABASE — tiny post-create update; `record_payment` does not carry
   * the document, so AddPaymentDialog records the payment (atomic double-entry)
   * and then attaches the uploaded proof here. No Nest endpoint benefit for this
   * tiny write-after-create operation.
   */
  async attachDocument(receiptId: string, companyId: string, documentUrl: string) {
    const { error } = await supabase
      .from("receipts")
      .update({ document_url: documentUrl })
      .eq("id", receiptId)
      .eq("company_id", companyId);

    if (error) {
      console.error("Error attaching receipt document:", serializeError(error));
      throw error;
    }
  },

  // ─── KEPT ON SUPABASE (deferred — see PR body) ───────────────────────────────

  /**
   * List receipts linked to a specific invoice (for InvoiceDetail receipts list).
   * KEPT ON SUPABASE — deferred to the InvoiceDetail slice.
   */
  async listByInvoice(invoiceId: string, companyId: string): Promise<{
    id: string;
    receipt_number: string | null;
    payment_date: string;
    amount: number | null;
    payment_channel: string | null;
  }[]> {
    const { data, error } = await supabase
      .from("receipts")
      .select("id, receipt_number, payment_date, amount, payment_channel")
      .eq("invoice_id", invoiceId)
      .eq("company_id", companyId)
      .is("deleted_at", null)
      .order("payment_date", { ascending: false });

    if (error) {
      console.error("Error fetching receipts by invoice:", serializeError(error));
      throw error;
    }

    return (data ?? []) as {
      id: string;
      receipt_number: string | null;
      payment_date: string;
      amount: number | null;
      payment_channel: string | null;
    }[];
  },

  /**
   * A property's receipts with unit/contract/invoice context, latest payment
   * first (the property-drawer Receipts tab). Company-scoped + soft-delete
   * filter; throws on error. KEPT on Supabase.
   */
  async listByProperty(companyId: string, propertyId: string) {
    const { data, error } = await supabase
      .from("receipts")
      .select(`
          *,
          unit:units(unit_number, unit_type),
          contract:contracts(contract_number),
          invoice:invoices(invoice_number)
        `)
      .eq("company_id", companyId)
      .eq("property_id", propertyId)
      .is("deleted_at", null)
      .order("payment_date", { ascending: false });
    if (error) throw error;
    return data;
  },

  /**
   * A contract's receipts (payment-history columns), payment-date ascending —
   * the contract-details payments table. Company-scoped + soft-delete filter;
   * throws on error.
   */
  async listByContract(contractId: string, companyId: string) {
    const { data, error } = await supabase
      .from("receipts")
      .select("id, receipt_number, invoice_id, payment_date, amount, payment_channel")
      .eq("contract_id", contractId)
      .eq("company_id", companyId)
      .is("deleted_at", null)
      .order("payment_date", { ascending: true });
    if (error) throw error;
    return data || [];
  },

  /**
   * Receipt statement rows (with property/unit names) for a set of
   * properties, latest payment first — the owner-details financial statement.
   * Company-scoped + soft-delete filter. NEVER throws: the pre-consolidation
   * site destructured `{ data }` only (statement renders without income
   * lines), so an error resolves to [].
   */
  async listStatementByProperties(companyId: string, propertyIds: string[]) {
    const { data } = await supabase
      .from("receipts")
      .select(
        `
          id,
          amount,
          payment_date,
          receipt_number,
          property_id,
          property:properties(name, name_en),
          unit:units(unit_number)
        `,
      )
      .eq("company_id", companyId)
      .in("property_id", propertyIds)
      .is("deleted_at", null)
      .order("payment_date", { ascending: false });
    return data ?? [];
  },

  /**
   * Owner-portal financial-summary receipts (used by useOwnerFinancials).
   * Matches by either property_id or contract_id (contracts always carry
   * owner_id; properties.owner_id may be null — same derivation as the
   * caller's contract-property resolution). Optional `range` bounds
   * `payment_date` — the "revenue actually received in this month" cut,
   * matching `listFinancial`'s payment_date convention below.
   *
   * Owner/tenant self-service reads stay on Supabase (not staff-flip scope —
   * see the file header); no Nest endpoint exists for this owner-scoped OR
   * filter.
   */
  async listForOwnerFinancials(
    propertyIds: string[],
    contractIds: string[],
    range?: { startDate?: string; endDate?: string },
  ): Promise<{ amount: number | null; payment_date: string | null; property_id: string | null }[]> {
    if (propertyIds.length === 0 && contractIds.length === 0) return [];

    const orFilter = [
      propertyIds.length > 0 ? `property_id.in.(${propertyIds.join(",")})` : null,
      contractIds.length > 0 ? `contract_id.in.(${contractIds.join(",")})` : null,
    ].filter(Boolean).join(",");

    let query = supabase
      .from("receipts")
      .select("amount, payment_date, property_id")
      .or(orFilter)
      .is("deleted_at", null);

    if (range?.startDate) query = query.gte("payment_date", range.startDate);
    if (range?.endDate) query = query.lte("payment_date", range.endDate);

    const { data, error } = await query;
    if (error) {
      console.error("Error fetching owner financials receipts:", serializeError(error));
      throw error;
    }

    return (data ?? []) as { amount: number | null; payment_date: string | null; property_id: string | null }[];
  },

  /**
   * Count receipts for a company (for dashboard stats).
   * KEPT ON SUPABASE — deferred to the finance-aggregates slice.
   */
  async count(companyId: string): Promise<number> {
    const { count, error } = await supabase
      .from("receipts")
      .select("id", { count: "exact", head: true })
      .eq("company_id", companyId)
      .is("deleted_at", null);

    if (error) {
      console.error("Error counting receipts:", serializeError(error));
      throw error;
    }

    return count ?? 0;
  },

  /**
   * Amount-only projection for useFinancialStats.
   * KEPT ON SUPABASE — deferred to the finance-aggregates slice.
   */
  async listStats(companyId: string): Promise<{ amount: number | null }[]> {
    const { data, error } = await supabase
      .from("receipts")
      .select("amount")
      .eq("company_id", companyId)
      .is("deleted_at", null);

    if (error) {
      console.error("Error fetching receipt stats:", serializeError(error));
      throw error;
    }

    return (data ?? []) as { amount: number | null }[];
  },

  /**
   * Summary projection for useFinanceSummaryStats (date-filtered).
   * KEPT ON SUPABASE — deferred to the finance-aggregates slice.
   */
  async listSummary(
    companyId: string,
    filter?: { startDate?: string; endDate?: string },
  ): Promise<{
    id: string;
    invoice_id: string | null;
    contract_id: string | null;
    created_at: string;
    amount: number | null;
    transaction_type: string | null;
  }[]> {
    let query = supabase
      .from("receipts")
      .select("id, invoice_id, contract_id, created_at, amount, transaction_type")
      .eq("company_id", companyId)
      .is("deleted_at", null);

    if (filter?.startDate) query = query.gte("created_at", filter.startDate);
    if (filter?.endDate) query = query.lte("created_at", filter.endDate);

    const { data, error } = await query;

    if (error) {
      console.error("Error fetching receipt summary:", serializeError(error));
      throw error;
    }

    return (data ?? []) as {
      id: string;
      invoice_id: string | null;
      contract_id: string | null;
      created_at: string;
      amount: number | null;
      transaction_type: string | null;
    }[];
  },

  /**
   * Monthly chart projection (payment_date filtered, gte).
   * KEPT ON SUPABASE — deferred to the finance-aggregates slice.
   */
  async listMonthly(
    companyId: string,
    sinceDate: string,
  ): Promise<{ amount: number | null; payment_date: string }[]> {
    const { data, error } = await supabase
      .from("receipts")
      .select("amount, payment_date")
      .eq("company_id", companyId)
      .is("deleted_at", null)
      .gte("payment_date", sinceDate);

    if (error) {
      console.error("Error fetching monthly receipts:", serializeError(error));
      throw error;
    }

    return (data ?? []) as { amount: number | null; payment_date: string }[];
  },

  /**
   * Filtered financial stats projection (date-filtered by payment_date).
   * KEPT ON SUPABASE — deferred to the finance-aggregates slice.
   */
  async listFinancial(
    companyId: string,
    filter?: { startDate?: string; endDate?: string },
  ): Promise<{ amount: number | null; payment_date: string | null }[]> {
    let query = supabase
      .from("receipts")
      .select("amount, payment_date")
      .eq("company_id", companyId)
      .is("deleted_at", null);

    if (filter?.startDate) query = query.gte("payment_date", filter.startDate);
    if (filter?.endDate) query = query.lte("payment_date", filter.endDate);

    const { data, error } = await query;

    if (error) {
      console.error("Error fetching receipt financial data:", serializeError(error));
      throw error;
    }

    return (data ?? []) as { amount: number | null; payment_date: string | null }[];
  },

  /**
   * Totals projection (created_at-filtered).
   * KEPT ON SUPABASE — deferred to the finance-aggregates slice.
   */
  async listTotals(
    companyId: string,
    filter?: { startDate?: string; endDate?: string },
  ): Promise<{ amount: number | null; created_at: string }[]> {
    let query = supabase
      .from("receipts")
      .select("amount, created_at")
      .eq("company_id", companyId)
      .is("deleted_at", null);

    if (filter?.startDate) query = query.gte("created_at", filter.startDate);
    if (filter?.endDate) query = query.lte("created_at", filter.endDate);

    const { data, error } = await query;

    if (error) {
      console.error("Error fetching receipt totals:", serializeError(error));
      throw error;
    }

    return (data ?? []) as { amount: number | null; created_at: string }[];
  },

  /**
   * Amount-only projection for owner payout calculation and reports.
   * KEPT ON SUPABASE — deferred to the finance-aggregates slice.
   */
  async listAmounts(companyId: string): Promise<{ amount: number | null }[]> {
    const { data, error } = await supabase
      .from("receipts")
      .select("amount")
      .eq("company_id", companyId)
      .is("deleted_at", null);

    if (error) {
      console.error("Error fetching receipt amounts:", serializeError(error));
      throw error;
    }

    return (data ?? []) as { amount: number | null }[];
  },

  /**
   * Property + amount + invoice_id projection for owner payout calculation.
   * `invoice_id` lets the caller classify each receipt via classifyInvoice
   * (rent/sale vs. a Dallali fee invoice billed on the same property) — a
   * plain property_id sum previously stacked fee receipts on top of the
   * owner's actual rent/sale revenue (#CEO 2026-07-23 finding).
   * KEPT ON SUPABASE — deferred to the finance-aggregates slice.
   */
  async listForOwnerPayouts(
    companyId: string,
  ): Promise<{ property_id: string | null; amount: number | null; invoice_id: string | null }[]> {
    const { data, error } = await supabase
      .from("receipts")
      .select("property_id, amount, invoice_id")
      .eq("company_id", companyId)
      .is("deleted_at", null)
      .not("property_id", "is", null);

    if (error) {
      console.error("Error fetching receipts for owner payouts:", serializeError(error));
      throw error;
    }

    return (data ?? []) as { property_id: string | null; amount: number | null; invoice_id: string | null }[];
  },

  /**
   * Income-statement / balance-sheet / cash-flow projection.
   * KEPT ON SUPABASE — deferred to the finance-aggregates slice.
   */
  async listForReports(
    companyId: string,
    extraFields?: "payment_channel",
  ): Promise<{ amount: number | null; transaction_type: string | null; payment_channel?: string | null }[]> {
    if (extraFields === "payment_channel") {
      const { data, error } = await supabase
        .from("receipts")
        .select("amount, transaction_type, payment_channel")
        .eq("company_id", companyId)
        .is("deleted_at", null);

      if (error) {
        console.error("Error fetching receipts for reports:", serializeError(error));
        throw error;
      }

      return (data ?? []) as { amount: number | null; transaction_type: string | null; payment_channel: string | null }[];
    }

    const { data, error } = await supabase
      .from("receipts")
      .select("amount, transaction_type")
      .eq("company_id", companyId)
      .is("deleted_at", null);

    if (error) {
      console.error("Error fetching receipts for reports:", serializeError(error));
      throw error;
    }

    return (data ?? []) as { amount: number | null; transaction_type: string | null }[];
  },

  /**
   * Recent receipts for the dashboard widget (RecentPayments).
   * KEPT ON SUPABASE — deferred to the finance-aggregates slice.
   */
  async listRecent(
    companyId: string,
    limit = 2,
  ): Promise<Array<{
    id: string;
    receipt_number: string | null;
    amount: number | null;
    payment_date: string;
    transaction_type: string | null;
    property: { name: string } | null;
    unit: { unit_number: string | null } | null;
    contract: {
      tenant: { first_name: string | null; last_name: string | null } | null;
      unit: { unit_number: string | null; property: { name: string } | null } | null;
    } | null;
  }>> {
    const { data, error } = await supabase
      .from("receipts")
      .select(`
        id,
        receipt_number,
        amount,
        payment_date,
        transaction_type,
        property:properties(name),
        unit:units(unit_number),
        contract:contracts(
          tenant:tenants(first_name, last_name),
          unit:units(unit_number, property:properties(name))
        )
      `)
      .eq("company_id", companyId)
      .is("deleted_at", null)
      .order("payment_date", { ascending: false })
      .limit(limit);

    if (error) {
      console.error("Error fetching recent receipts:", serializeError(error));
      throw error;
    }

    return (data ?? []) as unknown as Array<{
      id: string;
      receipt_number: string | null;
      amount: number | null;
      payment_date: string;
      transaction_type: string | null;
      property: { name: string } | null;
      unit: { unit_number: string | null } | null;
      contract: {
        tenant: { first_name: string | null; last_name: string | null } | null;
        unit: { unit_number: string | null; property: { name: string } | null } | null;
      } | null;
    }>;
  },

  /**
   * Collections report projection — rent receipts with invoice and tenant data.
   * KEPT ON SUPABASE — deferred to the finance-aggregates slice.
   */
  async listForCollections(companyId: string): Promise<Array<{
    id: string;
    amount: number | null;
    payment_date: string;
    payment_channel: string | null;
    receipt_number: string | null;
    reference_number: string | null;
    invoice: { total_amount: number | null; balance: number | null; tenant_id: string | null } | null;
    unit: { unit_number: string | null } | null;
    tenant: { full_name: string | null } | null;
  }>> {
    const { data, error } = await supabase
      .from("receipts")
      .select(`
        id, amount, payment_date, payment_channel, receipt_number, reference_number,
        invoice:invoices(total_amount, balance, tenant_id),
        unit:units(unit_number),
        tenant:profiles!receipts_tenant_id_fkey(full_name)
      `)
      .eq("company_id", companyId)
      .eq("transaction_type", "rent")
      .is("deleted_at", null)
      .order("payment_date", { ascending: false });

    if (error) {
      console.error("Error fetching collections receipts:", serializeError(error));
      throw error;
    }

    return (data ?? []) as unknown as Array<{
      id: string;
      amount: number | null;
      payment_date: string;
      payment_channel: string | null;
      receipt_number: string | null;
      reference_number: string | null;
      invoice: { total_amount: number | null; balance: number | null; tenant_id: string | null } | null;
      unit: { unit_number: string | null } | null;
      tenant: { full_name: string | null } | null;
    }>;
  },
};
